import React, { useEffect, useState } from 'react'

interface NetworkStatusProps {
  isOffline: boolean
  message: string
  visible: boolean
  onClose: () => void
}

export const NetworkStatus: React.FC<NetworkStatusProps> = ({ isOffline, message, visible, onClose }) => {
  const [isVisible, setIsVisible] = useState(visible)
  const [isMobile, setIsMobile] = useState(false)

  // Check if viewport is mobile size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 600)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => {
      window.removeEventListener('resize', checkMobile)
    }
  }, [])

  // Add keyframe animation to document
  useEffect(() => {
    const style = document.createElement('style')
    style.innerHTML = `
      @keyframes slideInToast {
        from {
          transform: translateY(100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
    `
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [])

  useEffect(() => {
    setIsVisible(visible)

    // Auto dismiss online toast after 3 seconds
    if (visible && !isOffline) {
      const timer = setTimeout(() => {
        setIsVisible(false)
        onClose()
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [visible, isOffline, onClose])

  if (!isVisible) return null

  const containerStyles: React.CSSProperties = {
    position: 'fixed',
    bottom: isMobile ? '16px' : '24px',
    left: isMobile ? '16px' : '24px',
    zIndex: 1501,
    maxWidth: '380px',
    width: isMobile ? 'calc(100% - 32px)' : '300px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
    borderRadius: '4px',
    overflow: 'hidden',
    backgroundColor: '#1A1A1A',
    animation: 'slideInToast 0.3s ease-out forwards',
  }

  const contentStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    position: 'relative',
  }

  const iconStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
    fontSize: '18px',
    color: isOffline ? '#f0b41e' : '#5aac44',
  }

  const messageContainerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  }

  const messageStyles: React.CSSProperties = {
    margin: 0,
    fontSize: '14px',
    fontWeight: 500,
    color: '#FFFFFF',
  }

  const submessageStyles: React.CSSProperties = {
    margin: '4px 0 0 0',
    fontSize: '12px',
    fontWeight: 400,
    color: '#9E9E9E',
    display: isOffline ? 'block' : 'none',
  }

  const closeButtonStyles: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: '#9E9E9E',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '4px',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '8px',
    display: isOffline ? 'none' : 'flex',
  }

  return (
    <div style={containerStyles}>
      <div style={contentStyles}>
        <div style={iconStyles}>
          {isOffline ? (
            <svg
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 576 512"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M569.517 440.013C587.975 472.007 564.806 512 527.94 512H48.054c-36.937 0-59.999-40.055-41.577-71.987L246.423 23.985c18.467-32.009 64.72-31.951 83.154 0l239.94 416.028zM288 354c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"></path>
            </svg>
          ) : (
            <svg
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 512 512"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"></path>
            </svg>
          )}
        </div>
        <div style={messageContainerStyles}>
          <p style={messageStyles}>{message}</p>
          {isOffline && <p style={submessageStyles}>Changes made now will not be saved.</p>}
        </div>
        {!isOffline && (
          <button onClick={onClose} style={closeButtonStyles} aria-label="Close">
            <svg
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 352 512"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"></path>
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}
